// Customize mode for a transaction — what the rendering components (list
// grid, record form, child grids, action bar) read while the mode is on.
// Provided by TxnScreen; `useTxnCustomize()` returns null when the mode is
// off, so every component renders exactly as before for everyone else.
//
// The mode has two scopes. 'org' is Customize (this organization's overlay).
// 'app' is Make changes (the transaction model itself, for everyone): the
// same gestures, plus adding / deleting fields and buttons — the `add*` /
// `openButton` hooks are present only then, and with scope 'org' nothing new
// renders.

import { createContext, useContext } from 'react'
import type { CustomizeSession } from './session'

export type CustomizeScope = 'org' | 'app'

export interface TxnCustomizeApi {
  session: CustomizeSession
  /** The owner-authored model (to tell a customized value from the standard). */
  core: unknown
  /** Open the ⚙ panel for one field of one form. */
  openField: (formNo: string, colKey: string, anchor: HTMLElement) => void
  /** The field whose panel is open (highlights its ⚙). */
  openFieldKey: string | null
  /** 'org' = Customize, 'app' = Make changes. */
  scope: CustomizeScope
  /** Make changes: add a field to a form, in a section (null = the first). */
  addField?: (formNo: string, group: string | null) => void
  /** Make changes: open a button's ⚙. `scopeKey` is a form's form_no, or
   *  'txn' for a transaction-level button. */
  openButton?: (scopeKey: string, name: string, anchor: HTMLElement) => void
  /** Make changes: add a button to a form (or 'txn'). */
  addButton?: (scopeKey: string) => void
  /** `${scopeKey}|${name}` of the button whose panel is open. */
  openButtonKey?: string | null
}

export const TxnCustomizeContext = createContext<TxnCustomizeApi | null>(null)

export function useTxnCustomize(): TxnCustomizeApi | null {
  return useContext(TxnCustomizeContext)
}
